import React, { useState } from "react";
import axios from "axios";

function resolveVideoSrc(url) {
  if (!url || typeof url !== "string") return url;
  if (url.startsWith("blob:") || url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/")) {
    const base = (process.env.REACT_APP_API_URL || "").trim().replace(/\/+$/, "") || window.location.origin;
    return base + url;
  }
  return url;
}

export default function VideoEditor() {
  const [video, setVideo] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [editedVideo, setEditedVideo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleVideoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setVideoFile(file);
      setVideo(URL.createObjectURL(file));
      setEditedVideo(null);
      setError(null);
    }
  };

  const handleEdit = async () => {
    if (!videoFile || !prompt.trim()) {
      setError("Please upload a video and enter an edit prompt.");
      return;
    }

    setLoading(true);
    setError(null);
    setEditedVideo(null);

    try {
      const formData = new FormData();
      formData.append("video", videoFile);
      formData.append("prompt", prompt);

      const apiUrl = process.env.REACT_APP_API_URL || '';
      const endpoint = apiUrl ? `${apiUrl}/chat/edit-video` : '/chat/edit-video';
      
      const response = await axios.post(
        endpoint,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.videoUrl) {
        setEditedVideo(response.data.videoUrl);
      } else if (response.data.message || response.data.instructions) {
        // Show instructions if video editing is not fully implemented
        setEditedVideo(null);
        setError(response.data.message || response.data.instructions || "Video editing instructions generated. Full video editing requires additional service integration.");
      } else {
        setError("Failed to edit video. Please try again.");
      }
    } catch (err) {
      console.error("Video editing error:", err);
      setError(
        err.response?.data?.error || "Failed to edit video. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 bg-white/20 backdrop-blur-md rounded-xl shadow-md w-full max-w-md">
      <h2 className="text-lg font-semibold mb-4">AI Video Editor</h2>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">
          Upload Video
        </label>
        <input
          type="file"
          accept="video/*"
          onChange={handleVideoUpload}
          className="mb-2 w-full text-sm"
        />
      </div>

      {video && (
        <div className="mb-4">
          <p className="text-sm font-medium mb-2">Original Video:</p>
          <video
            src={video}
            controls
            playsInline
            preload="metadata"
            className="w-full rounded-md border"
          />
        </div>
      )}

      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">
          Edit Prompt
        </label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Describe how you want to edit the video (e.g., 'add background music', 'remove background noise', 'add subtitles', 'speed up by 2x')"
          className="w-full p-2 border rounded-md text-sm"
          rows="3"
        />
      </div>

      <button
        onClick={handleEdit}
        disabled={loading || !videoFile || !prompt.trim()}
        className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed w-full"
      >
        {loading ? "Editing..." : "Apply AI Edit"}
      </button>

      {error && (
        <div className="mt-4 p-2 bg-red-100 text-red-700 rounded-md text-sm">
          {error}
        </div>
      )}

      {editedVideo && (
        <div className="mt-4">
          <p className="text-sm font-medium mb-2">Edited Video:</p>
          <video
            key={editedVideo}
            src={resolveVideoSrc(editedVideo)}
            controls
            playsInline
            preload="auto"
            className="w-full rounded-md border"
          />
          <a
            href={editedVideo}
            download="edited-video.mp4"
            className="mt-2 inline-block px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 text-sm text-center w-full"
          >
            Download Edited Video
          </a>
        </div>
      )}

      {!editedVideo && !error && loading === false && (
        <div className="mt-4 p-3 bg-blue-100 text-blue-700 rounded-md text-sm">
          <p className="font-medium mb-1">Video Editing Instructions:</p>
          <p>
            Video editing requires integration with external video processing services. The editing instructions
            have been generated and can be used with video editing software.
          </p>
        </div>
      )}

      <div className="mt-6 p-3 border border-dashed border-gray-300 rounded-md bg-white/40">
        <h3 className="text-sm font-semibold mb-2">Advanced video editing (Pro)</h3>
        <ul className="text-xs text-gray-700 space-y-1 list-disc pl-4">
          <li>
            <strong>Color grading</strong>: waveform/vectorscope monitoring, 3D LUT support, plus HDR and
            wide-gamut workflows (Rec.2020 / HDR10).
          </li>
          <li>
            <strong>Timeline & multi-cam</strong>: multi-camera edits, nested sequences/timelines, and proxy
            workflows for smooth 4K/8K editing.
          </li>
          <li>
            <strong>VFX & compositing</strong>: motion tracking, stabilization, chroma key/green screen, masking,
            rotoscoping, and particle-style effects.
          </li>
          <li>
            <strong>Audio & sound design</strong>: advanced mixing, EQ/compression, noise reduction, dialogue
            isolation, and 3D/spatial audio.
          </li>
          <li>
            <strong>Export & workflow</strong>: high bit-rate/codec support (ProRes, DNxHR, H.265, AV1), batch
            rendering, and collaboration-friendly workflows.
          </li>
        </ul>
      </div>
    </div>
  );
}
